import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GarminGlobalStoreService } from '@garmin-avcloud/avcloud-fly-web-common/shared';
import { map, mergeMap, Observable, of, tap } from 'rxjs';
import { UserConfigService } from 'src/app/core/services/config/user-config.service';
import { DIGITAL_STORE, Locale } from '../models/locale.model';

@Injectable()
export class LocaleService {
  private readonly FLY_LOCALE_URL: string = 'https://static.garmincdn.com/apps/fly/locales/fly-locales.json';

  private readonly BUY_GARMIN_USER_PREF = 'GarminUserPrefs=';
  private readonly BUY_GARMIN_LOCALE_PREF = 'GarminLocalePref=';
  private readonly BUY_GARMIN_PATH_DOMAIN = '; path=/; domain=.garmin.com;';

  private detectedLocale?: string;
  private detectedCountry?: string;
  private primaryAddressCountry?: string;

  private cache: Locale[] | null = null;

  constructor(
    userConfigService: UserConfigService,
    private readonly http: HttpClient,
    private readonly garminGlobalStoreService: GarminGlobalStoreService
  ) {
    userConfigService.getUserConfig().subscribe((config) => {
      this.detectedLocale = config.detectedLocale;
      this.detectedCountry = config.detectedCountry;
      this.primaryAddressCountry = config.primaryAddressCountry;
    });

    this.getLocales();
  }

  getLocales(): Observable<Locale[]> {
    if (this.cache != null) {
      return of(this.cache);
    }
    return this.load();
  }

  getSortedLocales(): Observable<Locale[]> {
    return this.getLocales().pipe(
      map((locales: Locale[]) => {
        return locales.sort((a, b) => {
          const localeNameA = a.localeName.toUpperCase(); // ignore upper and lowercase
          const localeNameB = b.localeName.toUpperCase(); // ignore upper and lowercase
          if (localeNameA == 'OTHER COUNTRIES') {
            return 1;
          }
          if (localeNameB == 'OTHER COUNTRIES') {
            return -1;
          }
          if (localeNameA < localeNameB) {
            return -1;
          }
          if (localeNameA > localeNameB) {
            return 1;
          }

          // localeNames must be equal
          return 0;
        });
      })
    );
  }

  flyLocale(desiredLocale: string): Observable<Locale> {
    return this.getLocales().pipe(
      map((locales) => locales.find((locale: Locale) => locale.locale === desiredLocale)),
      mergeMap((locale) => (locale != null ? of(locale) : this.searchLocaleByCountry(desiredLocale))),
      map((locale) => (locale != null ? locale : DIGITAL_STORE))
    );
  }

  getSelectedLocale(): Observable<Locale> {
    const localeValue = this.garminGlobalStoreService.getGlobal('locale');
    if (typeof localeValue === 'string') {
      return this.flyLocale(localeValue);
    } else if (this.detectedLocale != null) {
      return this.flyLocale(this.detectedLocale);
    } else if (this.detectedCountry != null) {
      return this.flyLocale(this.detectedCountry);
    } else {
      return this.flyLocale('en-US');
    }
  }

  selectLocale(newLocale: Locale): void {
    const cookieLocale = newLocale.locale;
    this.garminGlobalStoreService.setGlobal('locale', cookieLocale);

    document.cookie = this.BUY_GARMIN_USER_PREF + cookieLocale + this.BUY_GARMIN_PATH_DOMAIN;
    const cookieLocaleUnder = cookieLocale.replace('-', '_');
    document.cookie = this.BUY_GARMIN_LOCALE_PREF + cookieLocaleUnder + this.BUY_GARMIN_PATH_DOMAIN;
  }

  getAutoRenewalLocale(): Observable<Locale> {
    if (this.primaryAddressCountry != null) {
      return this.searchLocaleByCountry(this.primaryAddressCountry).pipe(
        mergeMap((locale) => (locale != null ? of(locale) : this.getSelectedLocale()))
      );
    } else {
      return this.getSelectedLocale();
    }
  }

  private searchLocaleByCountry(country: string): Observable<Locale | null> {
    return this.getLocales().pipe(
      map((locales) => {
        const splitIndex = country.indexOf('-');
        const countryCode = splitIndex !== -1 ? country.substring(splitIndex + 1) : country;
        return (
          locales.find((locale: Locale) => {
            const storeSplitIndex = locale.locale.indexOf('-') + 1;
            return countryCode === locale.locale.substring(storeSplitIndex);
          }) ?? null
        );
      })
    );
  }

  private load(): Observable<any> {
    return this.http.get<Locale[]>(this.FLY_LOCALE_URL, {}).pipe(tap((content) => (this.cache = content)));
  }
}
